/**
 * The sample data template a system admin edits under /admin/sample-data.
 *
 * Every row carries a stable `key` so other rows can point at it (a call names a player by key, a
 * player names a team by key). Keys are generated client-side and never shown to the admin.
 */

export interface SampleTeam {
  key: string;
  name: string;
}

export interface SamplePlayer {
  key: string;
  firstName: string;
  lastName: string;
  position: string;
  gradYear: number | null;
  phone: string;
  email: string;
  teamKey: string | null;
}

export interface SampleRosterPlacement {
  playerKey: string;
  position: string;
  sortOrder: number;
}

export interface SampleRosterBoard {
  name: string;
  placements: SampleRosterPlacement[];
}

export interface SampleCall {
  key: string;
  playerKey: string;
  dayOffset: number;
  hour: number;
}

export interface SampleNote {
  key: string;
  playerKey: string;
  content: string;
}

export interface SampleGame {
  key: string;
  dayOffset: number;
  /** HH:mm, local to the template's time zone. */
  time: string;
  fieldName: string;
  homeTeamName: string;
  awayTeamName: string;
}

export interface SampleEvent {
  key: string;
  title: string;
  startDayOffset: number;
  endDayOffset: number;
  games: SampleGame[];
}

export interface SampleDataTemplateContent {
  timeZone: string;
  teams: SampleTeam[];
  players: SamplePlayer[];
  rosterBoard: SampleRosterBoard | null;
  calls: SampleCall[];
  notes: SampleNote[];
  events: SampleEvent[];
  duplicatePairPlayerKeys: [string, string] | null;
}

export interface SampleDataTemplateResponse {
  content: SampleDataTemplateContent;
  updatedAt: string | null;
}

export interface SampleDataTemplateRequest {
  content: SampleDataTemplateContent;
}

/** One validation failure. `field` is a path such as `players[0].phone` or `duplicatePairPlayerKeys`. */
export interface SampleDataFieldError {
  field: string;
  message: string;
}

export type SaveSampleDataTemplateResult =
  | { ok: true; template: SampleDataTemplateResponse }
  | { ok: false; errors: SampleDataFieldError[] };

export const EMPTY_SAMPLE_DATA_TEMPLATE: SampleDataTemplateContent = {
  timeZone: 'America/New_York',
  teams: [],
  players: [],
  rosterBoard: null,
  calls: [],
  notes: [],
  events: [],
  duplicatePairPlayerKeys: null,
};

/*
 * Per-organization sample data: whether an organization has a seeded set, what it contains, and
 * what removing it would leave behind. System admin only.
 */

/** Result of seeding during organization creation. Absent when the API predates it. */
export type OrganizationSampleDataOutcome = 'seeded' | 'failed' | 'skipped';

export type SampleDataCallsStatus = 'NotApplicable' | 'Pending' | 'Seeding' | 'Seeded' | 'Failed';

export interface SampleDataCounts {
  teams: number;
  prospects: number;
  rosterBoards: number;
  rosterBoardPlayers: number;
  notes: number;
  events: number;
  calls: number;
}

/** A sample record that removal leaves in place, because a coach built real data on top of it. */
export interface SampleDataSkippedItem {
  entityType: string;
  label: string;
  reason: string;
}

export interface SampleDataRemovalPreview {
  removable: number;
  skipped: SampleDataSkippedItem[];
}

export interface OrganizationSampleDataStatus {
  active: boolean;
  seededAt: string | null;
  counts: SampleDataCounts;
  callsStatus: SampleDataCallsStatus | null;
  callsError: string | null;
  removalPreview: SampleDataRemovalPreview;
}

export interface RemoveSampleDataResult {
  removed: number;
  skipped: SampleDataSkippedItem[];
  /** Null when the response carried no usable status, so the caller should fetch it again. */
  status: OrganizationSampleDataStatus | null;
}
